/**
 * Centralized localStorage management for demo mode feature
 * Provides type-safe access to demo mode related localStorage keys
 */

// localStorage key constants
export const DEMO_MODE_STORAGE_KEYS = {
  HAS_SEEN_NUDGE: 'hasSeenDemoNudge',
  HAS_VISITED_DEMO: 'hasVisitedDemo',
  HAS_SEEN_WELCOME: 'hasSeenDemoWelcome',
  IS_TOUR_COMPLETED: 'isDemoTourCompleted',
  HAS_SEEN_SPOTLIGHT: 'hasSeenDemoSpotlight',
} as const;

/**
 * Type-safe localStorage getter
 */
const getStorageValue = (key: string): boolean => {
  try {
    return localStorage.getItem(key) === 'true';
  } catch (error) {
    console.error(`Error reading localStorage key: ${key}`, error);
    return false;
  }
};

/**
 * Type-safe localStorage setter
 */
const setStorageValue = (key: string, value: boolean): void => {
  try {
    localStorage.setItem(key, value.toString());
  } catch (error) {
    console.error(`Error writing localStorage key: ${key}`, error);
  }
};

/**
 * Demo mode localStorage utilities
 * Provides a clean API for managing demo mode state
 */
export const demoModeStorage = {
  // Getters
  hasSeenNudge: (): boolean =>
    getStorageValue(DEMO_MODE_STORAGE_KEYS.HAS_SEEN_NUDGE),

  hasVisitedDemo: (): boolean =>
    getStorageValue(DEMO_MODE_STORAGE_KEYS.HAS_VISITED_DEMO),

  hasSeenWelcome: (): boolean =>
    getStorageValue(DEMO_MODE_STORAGE_KEYS.HAS_SEEN_WELCOME),

  isTourCompleted: (): boolean =>
    getStorageValue(DEMO_MODE_STORAGE_KEYS.IS_TOUR_COMPLETED),

  hasSeenSpotlight: (): boolean =>
    getStorageValue(DEMO_MODE_STORAGE_KEYS.HAS_SEEN_SPOTLIGHT),

  // Setters
  markNudgeAsSeen: (): void =>
    setStorageValue(DEMO_MODE_STORAGE_KEYS.HAS_SEEN_NUDGE, true),

  markDemoAsVisited: (): void =>
    setStorageValue(DEMO_MODE_STORAGE_KEYS.HAS_VISITED_DEMO, true),

  markWelcomeAsSeen: (): void =>
    setStorageValue(DEMO_MODE_STORAGE_KEYS.HAS_SEEN_WELCOME, true),

  markTourAsCompleted: (): void =>
    setStorageValue(DEMO_MODE_STORAGE_KEYS.IS_TOUR_COMPLETED, true),

  markSpotlightAsSeen: (): void =>
    setStorageValue(DEMO_MODE_STORAGE_KEYS.HAS_SEEN_SPOTLIGHT, true),

  // Combined actions
  markNudgeAccepted: (): void => {
    demoModeStorage.markNudgeAsSeen();
    demoModeStorage.markDemoAsVisited();
  },

  // Utility to check if user should see the nudge
  shouldShowNudge: (hasDataSources: boolean, isDemoMode: boolean): boolean => {
    return !demoModeStorage.hasSeenNudge() && !hasDataSources && !isDemoMode;
  },

  // Utility to check if user should see the welcome modal
  shouldShowWelcome: (isDemoMode: boolean): boolean => {
    return (
      isDemoMode &&
      demoModeStorage.hasVisitedDemo() &&
      !demoModeStorage.isTourCompleted() &&
      !demoModeStorage.hasSeenWelcome()
    );
  },

  // Utility to check if tour should run
  shouldRunTour: (): boolean => {
    return (
      demoModeStorage.hasVisitedDemo() && !demoModeStorage.isTourCompleted()
    );
  },

  // Utility to check if spotlight should show
  shouldShowSpotlight: (): boolean => {
    return !demoModeStorage.hasSeenSpotlight();
  },

  // Clear all demo mode storage (useful for testing)
  clear: (): void => {
    try {
      Object.values(DEMO_MODE_STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      console.error('Error clearing demo mode storage', error);
    }
  },
};
